import React, { useState, createContext, useContext, useEffect } from "react";
import { View, Text, StyleSheet } from "react-native";
import { useTheme } from "../../context/ThemeContext";
import ProfileHeader from "./components/ProfileHeader";
import { ProfilePageType } from "../profile";
import { useComponentEditor } from "../../context/ComponentEditorContext";
import { useApp } from "../../context/AppContext";
import { useContent } from "../../context/ContentContext";
import ScrollableCanvas from "./components/ScrollableCanvas";

// Create context for profile page state
export const ProfilePageContext = createContext(null);

// Custom hook for components to access profile page state
export const useProfilePage = () => {
	const context = useContext(ProfilePageContext);
	if (!context) {
		throw new Error("useProfilePage must be used within a ProfilePageProvider");
	}
	return context;
};

const ProfilePage = ({ content = {} }) => {
	const theme = useTheme();
	const styles = useStyles();
	const { selectedComponent, onPageChange, activeTab: activeTabFromEditor, setActiveTab: setActiveTabFromEditor } = useComponentEditor();
	const { activePage } = useApp();
	const { fetchContent, content: contentFromContext } = useContent();
	const { tabs } = fetchContent("profileHeader");

	// Merge with default content and store in state
	const defaultContent = ProfilePageType.getDefaultContent();

	// Set initial active tab to first tab if available, otherwise null
	const [activeTab, setActiveTab] = useState(tabs.length > 0 ? tabs[0].id : null);

	useEffect(() => {
		setActiveTabFromEditor(activeTab);
	}, [activeTab]);

	// Store tab content in state
	const [tabContents, setTabContents] = useState(() => {
		const contents = {};
		tabs.forEach((tab) => {
			contents[tab.id] = {
				sections: [],
				layout: "default",
			};
		});
		return contents;
	});

	const handleTabPress = (tabId) => {
		setActiveTab(tabId);
		setActiveTabFromEditor(tabId);
	};

	// Get editable properties for a component type
	const getComponentProperties = (componentType) => {
		switch (componentType) {
			case "ProfileHeader":
				return {
					sections: [
						{
							id: "stats",
							title: "Stats",
							properties: ["statsValueFont", "statsLabelFont", "statsLabels"],
						},
						{
							id: "picture",
							title: "Picture",
							properties: ["avatarStyle", "avatarSize"],
						},
						{
							id: "info",
							title: "Info",
							properties: ["name", "bio", "showLocation"],
						},
						{
							id: "tabs",
							title: "Tabs",
							properties: ["tabsConfig", "tabsSharedStyles"],
						},
					],
					properties: {
						statsValueFont: {
							type: "font",
							label: "Value Style",
							value: {
								family: "System",
								size: 16,
								color: theme.colors.text,
								weight: "600",
							},
							description: "Font style for the stats values",
						},
						statsLabelFont: {
							type: "font",
							label: "Label Style",
							value: {
								family: "System",
								size: 12,
								color: theme.colors.textSecondary,
								weight: "400",
							},
							description: "Font style for the stats labels",
						},
						statsLabels: {
							type: "group",
							label: "Labels",
							properties: {
								posts: {
									type: "text",
									label: "Posts Label",
									value: "Posts",
								},
								followers: {
									type: "text",
									label: "Followers Label",
									value: "Followers",
								},
								following: {
									type: "text",
									label: "Following Label",
									value: "Following",
								},
							},
							description: "Customize the stats labels",
						},
						avatarStyle: {
							type: "select",
							label: "Avatar Style",
							value: "circle",
							options: ["circle", "square"],
							description: "Shape of the avatar image",
						},
						avatarSize: {
							type: "number",
							label: "Avatar Size",
							value: 77,
							min: 40,
							max: 150,
							description: "Size of the avatar in pixels",
						},
						name: {
							type: "text",
							label: "Display Name",
							value: pageState.content.profile.name || "",
							description: "User's display name",
						},
						bio: {
							type: "textarea",
							label: "Bio",
							value: pageState.content.profile.bio || "",
							description: "User's biography",
						},
						showLocation: {
							type: "boolean",
							label: "Show Location",
							value: true,
							description: "Whether to display the location information",
						},
						tabsConfig: {
							type: "tabs",
							label: "Navigation Tabs",
							description: "Configure individual tab labels and icons",
							value: pageState.content.profile.tabsConfig || [],
						},
						tabsSharedStyles: {
							type: "group",
							label: "Shared Tab Styles",
							properties: {
								showIcon: {
									type: "boolean",
									label: "Show Icons",
									value: true,
									description: "Show icons for all tabs",
								},
								showLabel: {
									type: "boolean",
									label: "Show Labels",
									value: true,
									description: "Show labels for all tabs",
								},
								font: {
									type: "font",
									label: "Font Style",
									value: {
										family: "System",
										size: 14,
										weight: "400",
										color: "#2a2a2a",
									},
									description: "Font style applied to all tabs",
								},
								backgroundColor: {
									type: "color",
									label: "Background Color",
									value: "#FFFFFF",
									description: "Background color for all tabs",
								},
							},
							description: "Styles applied to all tabs",
						},
					},
				};
			default:
				return null;
		}
	};

	// Handle temporary style updates (while editing)

	// Apply temporary styles to permanent styles

	const contextValue = {
		activeTab,
		handleTabPress,
		getComponentProperties,
	};

	// If no tabs are configured, show a message
	if (tabs.length === 0) {
		return (
			<ProfilePageContext.Provider value={contextValue}>
				<View style={styles.container}>
					<ProfileHeader />
					<View style={styles.emptyCanvas}>
						<Text style={styles.emptyCanvasText}>No Tabs Configured</Text>
						<Text style={styles.emptyCanvasSubtext}>Add tabs to start building your profile page</Text>
					</View>
				</View>
			</ProfilePageContext.Provider>
		);
	}

	return (
		<ProfilePageContext.Provider value={contextValue}>
			<View style={styles.container}>
				<ProfileHeader />
				{activeTab && (
					<View style={styles.content}>
						<ScrollableCanvas activeTab={activeTab} />
					</View>
				)}
			</View>
		</ProfilePageContext.Provider>
	);
};

export default ProfilePage;

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			flex: 1,
			backgroundColor: theme.colors.background,
		},
		postContainer: {
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.lg,
			marginBottom: theme.spacing.md,
			padding: theme.spacing.md,
			shadowColor: "#000",
			shadowOffset: { width: 0, height: 1 },
			shadowOpacity: 0.05,
			shadowRadius: 3,
			elevation: 2,
		},
		content: {
			flex: 1,
			padding: theme.spacing.md,
		},
		emptyCanvas: {
			flex: 1,
			justifyContent: "center",
			alignItems: "center",
			minHeight: 300,
			backgroundColor: theme.colors.systemBackground.secondary,
			borderRadius: theme.borderRadius.lg,
			padding: theme.spacing.xl,
			marginVertical: theme.spacing.md,
			borderWidth: 2,
			borderStyle: "dashed",
			borderColor: theme.colors.separator,
		},
		emptyCanvasText: {
			...theme.typography.title3,
			color: theme.colors.textSecondary,
			marginBottom: theme.spacing.sm,
		},
		emptyCanvasSubtext: {
			...theme.typography.caption1,
			color: theme.colors.textTertiary,
		},
	});
};
